/**
 * Utilities for translating enum values to user-friendly display names
 */

import type {
    ResidenceOwnership,
    ResidenceType,
    EmploymentStatus,
    Citizenship,
    SpokenLanguage,
} from '@servicetitan/financing-api-client';

/**
 * Generic type for enum translation dictionaries
 */
export type EnumTranslations<T extends string | number> = Partial<Record<T, string>>;

/**
 * Generic function to get translated enum value with fallback
 */
export function translateEnum<T extends string | number>(
    enumValue: T,
    translations: EnumTranslations<T>,
    fallback?: string,
): string {
    return translations[enumValue] ?? fallback ?? String(enumValue);
}

/**
 * Residence ownership translations
 */
export const residenceOwnershipTranslations: EnumTranslations<ResidenceOwnership> = {
    notSet: 'Not specified',
    own: 'Own outright',
    ownWithMortgage: 'Own with mortgage',
    rent: 'Rent',
    other: 'Other',
} as const;

/**
 * Residence type translations
 */
export const residenceTypeTranslations: EnumTranslations<ResidenceType> = {
    notSet: 'Not specified',
    primary: 'Primary residence',
    secondary: 'Secondary or vacation home',
    investment: 'Investment property',
} as const;

/**
 * Employment status translations
 */
export const employmentStatusTranslations: EnumTranslations<EmploymentStatus> = {
    notSet: 'Not specified',
    employed: 'Employed',
    retired: 'Retired',
    notEmployed: 'Not employed',
    other: 'Other',
} as const;

/**
 * Citizenship status translations
 */
export const citizenshipTranslations: EnumTranslations<Citizenship> = {
    notSet: 'Not specified',
    usCitizen: 'U.S. citizen',
    lawfulPermanentResident: 'Lawful permanent resident',
    other: 'Other',
} as const;

/**
 * Spoken language translations
 */
export const spokenLanguageTranslations: EnumTranslations<SpokenLanguage> = {
    notSet: 'Not specified',
    english: 'English',
    spanish: 'Spanish',
    other: 'Other',
} as const;

/**
 * Static class for enum translations with organized methods
 */
export class Translate {
    /**
     * Translate residence ownership values
     */
    static residenceOwnership(value: ResidenceOwnership): string {
        return translateEnum(value, residenceOwnershipTranslations);
    }

    /**
     * Translate residence type values
     */
    static residenceType(value: ResidenceType): string {
        return translateEnum(value, residenceTypeTranslations);
    }

    /**
     * Translate employment status values
     */
    static employmentStatus(value: EmploymentStatus): string {
        return translateEnum(value, employmentStatusTranslations);
    }

    /**
     * Translate citizenship status values
     */
    static citizenship(value: Citizenship): string {
        return translateEnum(value, citizenshipTranslations);
    }

    /**
     * Translate spoken language values
     */
    static spokenLanguage(value: SpokenLanguage): string {
        return translateEnum(value, spokenLanguageTranslations);
    }
}
